<script setup lang="ts">
import { money } from '@nuts/auto-delivery-sdk/dist/utils/format';

import AutoDeliveryFAQItem from '@/components/auto-delivery/faq/AutoDeliveryFAQItem.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { FLAT_RATE_THRESHOLD } from '@/lib/shipping';

const { flags } = useFeatureFlags();

const flatRateThresholdFormatted = money(FLAT_RATE_THRESHOLD).replace('.00', '');
</script>

<template>
  <ul
    class="flex flex-col w-full border-solid divide-y border-y border-neutral-300 divide-solid divide-neutral-300"
  >
    <template v-if="flags.autoDeliveryPortal">
      <AutoDeliveryFAQItem id="ad-faq-how-to-cancel">
        <template #question>How do I cancel my subscription?</template>
        <template #answer>
          <p>
            You can cancel your subscription anytime through the Auto-Delivery portal. Follow these
            steps:
          </p>
          <ul class="pl-6 list-decimal list-outside">
            <li>Visit the <strong>Auto-Delivery Overview</strong> portal.</li>
            <li>
              Click the <strong>View Details & Make Changes</strong> button on your subscription
              summary.
            </li>
            <li>
              On the <strong>Subscription Details</strong> page, scroll to the item you want to
              cancel and click Manage.
            </li>
            <li>In the menu that opens, select <strong>Cancel Product Subscription</strong>.</li>
            <li>Confirm your cancellation in the pop-up modal.</li>
          </ul>
          <p class="mt-8">
            If you cancel all items in a subscription group, the entire subscription will be
            deleted. You’ll receive an email confirmation of the cancellation. Please allow up to 24
            hours for changes to take effect.
          </p>
        </template>
      </AutoDeliveryFAQItem>
      <AutoDeliveryFAQItem id="ad-faq-how-to-add">
        <template #question>How do I add products to my subscription?</template>
        <template #answer>
          <p>There are two ways to add items to Auto-Delivery:</p>
          <ul class="pl-6 list-decimal list-outside">
            <li>
              <strong>From the Product Page:</strong> Visit the product page on our site and click
              the "Auto-Delivery" button to add the item to your subscription..
            </li>
            <li>
              <strong>Through the Auto-Delivery Portal: </strong> Access the Auto-Delivery portal to
              add items directly to your subscription.
            </li>
          </ul>
          <p class="mt-4">Both methods make it simple to customize your Auto-Delivery orders.</p>
        </template>
      </AutoDeliveryFAQItem>
      <AutoDeliveryFAQItem id="ad-faq-reminders">
        <template #question>Will I receive a reminder before my subscription ships?</template>
        <template #answer>
          Three days before your Auto-Delivery package ships, you'll receive a reminder email,
          giving you the opportunity to make any last-minute changes, cancel, or pause a
          subscription if needed.
        </template>
      </AutoDeliveryFAQItem>
      <AutoDeliveryFAQItem id="ad-faq-shipping">
        <template #question
          >What happens if one of my Auto-Delivery products is out of stock?</template
        >
        <template #answer>
          If one of your Auto-Delivery items is out of stock, we'll notify you in your upcoming
          shipment reminder email. You can choose a one-time backup product to replace the
          out-of-stock item or permanently swap it for a different product.
        </template>
      </AutoDeliveryFAQItem>
      <AutoDeliveryFAQItem id="ad-faq-how-to-remove">
        <template #question>Is shipping free for Auto-Delivery?</template>
        <template #answer>
          Free shipping applies when your subscription order totals {{ flatRateThresholdFormatted }}
          or more, including by-the-case items. To qualify, all items must ship on the same day to
          the addresses you’ve provided.
        </template>
      </AutoDeliveryFAQItem>
      <AutoDeliveryFAQItem id="ad-faq-how-to-remove">
        <template #question>How can I remove products from my subscription?</template>
        <template #answer>
          <p>
            Each product page features the option to select Auto-Delivery and the delivery
            frequency. To add an item, visit its product page and click the Add to Auto-Delivery
            button.
          </p>
          <p>
            To remove items, visit the Auto-Delivery page on Your Account, find the items you wish
            to eliminate and then click on the Remove From This Shipment button.
          </p>
        </template>
      </AutoDeliveryFAQItem>
      <AutoDeliveryFAQItem id="ad-faq-how-to-remove">
        <template #question>Can I set up two subscriptions with different frequencies?</template>
        <template #answer>
          Yes, you can set up two subscriptions with different frequencies. You can do this by
          adjusting the frequency of an existing shipment or by making a separate purchase on
          Nuts.com and selecting a different auto-delivery frequency at checkout.
        </template>
      </AutoDeliveryFAQItem>
      <AutoDeliveryFAQItem id="ad-faq-how-to-remove">
        <template #question>What is a one-time Auto-Delivery shipment?</template>
        <template #answer>
          A one-time Auto-Delivery shipment allows you to add one or more items to your upcoming
          subscription shipment. These items will ship together with your subscription products in
          the same box, letting you make one-off purchases without paying additional shipping fees.
          These items will not be added to your recurring subscription schedule.
        </template>
      </AutoDeliveryFAQItem>
      <AutoDeliveryFAQItem id="ad-faq-how-to-remove">
        <template #question
          >Can my 'every 4 weeks' and 'every 8 weeks' shipments arrive in one box?</template
        >
        <template #answer>
          <p>
            Yes, your 'Every 4 Weeks' and 'Every 8 Weeks' subscription shipments can arrive in one
            box if they are scheduled to ship on the same day. By aligning their shipment dates,
            both orders will be combined into a single box, and you’ll only pay one shipping fee. If
            the combined total of the shipments reaches {{ flatRateThresholdFormatted }}
            or more, you’ll also qualify for free shipping.
          </p>
          <span><strong>Example:</strong></span>
          <ul class="pl-6 list-disc list-outside">
            <li>The 'Every 4 Weeks' shipment totals $32.</li>
            <li>The 'Every 8 Weeks' shipment totals $40.</li>
            <li>
              Scheduling both to ship on the same day brings the total to $72, which qualifies for
              free shipping.
            </li>
          </ul>
          <p class="mt-4">
            Combining shipments saves on shipping costs and ensures all your items arrive together
            in one convenient package!
          </p>
        </template>
      </AutoDeliveryFAQItem>
      <AutoDeliveryFAQItem id="ad-faq-how-to-remove">
        <template #question>Help! I’m not seeing my Auto-Delivery shipment notifications</template>
        <template #answer>
          <p>
            If you're not receiving Auto-Delivery shipment notifications, first check the spam or
            junk folder in your email, as sometimes these reminders may end up there. You can also
            manage your notification preferences directly in the Auto-Delivery portal. Just navigate
            to the Auto-Delivery Settings tab and click Adjust Shipment Reminders to customize how
            and when you receive notifications.
          </p>
        </template>
      </AutoDeliveryFAQItem>
    </template>
    <template v-else>
      <AutoDeliveryFAQItem id="ad-faq-how-to-cancel">
        <template #question>How do I cancel my Subscription?</template>
        <template #answer>
          Visit the <a href="/account/auto-delivery">Auto-Delivery page</a> and click the Cancel
          subscription button. You'll be prompted to confirm this action and indicate your reason
          for canceling. Once you've submitted, your subscription will be canceled and no future
          orders will be placed. You will also receive an email notification confirming this. Please
          allow up to 24 hours for any modifications or cancellations to be processed.
        </template>
      </AutoDeliveryFAQItem>
      <AutoDeliveryFAQItem id="ad-faq-how-to-add">
        <template #question>How do I add products to my subscription?</template>
        <template #answer>
          Each product page features the option to select Auto-Delivery and the delivery frequency.
          To add an item, visit its product page and click the Add to Auto-Delivery button.
        </template>
      </AutoDeliveryFAQItem>
      <AutoDeliveryFAQItem id="ad-faq-reminders">
        <template #question>Will I get shipping reminders?</template>
        <template #answer>
          Three days before your Auto-Delivery package ships out, you will receive a reminder via
          email so you can make any last minute changes / cancel or pause a subscription if needed.
        </template>
      </AutoDeliveryFAQItem>
      <AutoDeliveryFAQItem id="ad-faq-shipping">
        <template #question>Is Auto-Delivery Shipping FREE?</template>
        <template #answer>
          We'll apply free shipping when you spend
          {{ money(FLAT_RATE_THRESHOLD).replace('.00', '') }} or more on subscription products,
          including by-the-case items. All items must be set to ship on the same day to all the
          addresses you provide.
        </template>
      </AutoDeliveryFAQItem>
      <AutoDeliveryFAQItem id="ad-faq-how-to-remove">
        <template #question>How do I remove products from my subscription?</template>
        <template #answer>
          Each product page features the option to select Auto-Delivery and the delivery frequency.
          To add an item, visit its product page and click the Add to Auto-Delivery button.
          <br />
          <br />
          To remove items, visit the <a href="/account/auto-delivery">Auto-Delivery page</a> on
          <a href="/account">Your Account</a>, find the items you wish to eliminate and then click
          on the Remove From This Shipment button.
        </template>
      </AutoDeliveryFAQItem>
    </template>
  </ul>
</template>
